import { Box, Flex, Link, useToast } from '@lego/klik-ui';
import { useCallback, useEffect, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import type { DetailSectionProps } from '@/components/common/details/DetailsBox';
import { EditableDetailsBox } from '@/components/common/details/EditableDetailsBox';
import { EditButtons } from '@/components/common/EditButtons';
import { UserNameLink } from '@/components/common/UserNameLink';
import type { ApplicationDetailedResponse, UpdateApplicationRequest } from '@/hooks/application';
import { useUpdateApplication } from '@/hooks/application';
import { useApplicationVersions } from '@/hooks/application_version';
import { useCanWriteById, useWorkspace } from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';
import { paths } from '@/utility/global_paths';
import { ToastFactory } from '@/utility/toast_factory';
import { applicationNameSchema } from '@/validation/application';
import { WorkspaceTextLink } from '../workspace/WorkspaceTextLink';
import { Ag2AppIdSelect } from './Ag2AppIdSelect';
import { StableVersionSelect } from './StableVersionSelect';

const AG2_BASE_URL = 'https://omnisight.thelegogroup.com/architecture-gateway/applications';

const FIELD_NAME_MAP: Record<string, string> = {
  Name: 'name',
  Description: 'description',
};

const buildUpdatePayload = (
  application: ApplicationDetailedResponse,
  overrides: Partial<UpdateApplicationRequest>
): UpdateApplicationRequest => ({
  name: application.name,
  description: application.description,
  stableVersionId: application.stableVersionId,
  ag2AppId: application.ag2AppId,
  ...overrides,
});

type Props = {
  application: ApplicationDetailedResponse;
};

export const EditableApplicationDetails = ({ application }: Props) => {
  const [editedValue, setEditedValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isEditingAg2AppId, setIsEditingAg2AppId] = useState(false);
  const [pendingAg2AppId, setPendingAg2AppId] = useState<string | null>(application.ag2AppId);
  const [isEditingStableVersion, setIsEditingStableVersion] = useState(false);
  const [pendingStableVersionId, setPendingStableVersionId] = useState<number | null>(
    application.stableVersionId
  );
  const [isSaving, setIsSaving] = useState(false);

  const canWrite = useCanWriteById(application.workspaceId);
  const { data: workspaceResponse, isLoading: isWorkspaceLoading } = useWorkspace(
    application.workspaceId
  );
  const { data: versionsResponse } = useApplicationVersions(
    application.workspaceId,
    application.id
  );
  const { mutate } = useSWRConfig();
  const updateApplication = useUpdateApplication(application.workspaceId, application.id);
  const toast = useToast();

  const cacheKey = `workspaces/${application.workspaceId}/applications/${application.id}`;
  const workspace = workspaceResponse;
  const versions = versionsResponse?.applicationVersions ?? [];

  useEffect(() => {
    if (!isEditingAg2AppId) {
      setPendingAg2AppId(application.ag2AppId);
    }
  }, [application.ag2AppId, isEditingAg2AppId]);

  useEffect(() => {
    if (!isEditingStableVersion) {
      setPendingStableVersionId(application.stableVersionId);
    }
  }, [application.stableVersionId, isEditingStableVersion]);

  const handleStartEdit = useCallback(
    (key: string) => {
      if (key === 'Name') {
        setEditedValue(application.name);
      } else if (key === 'Description') {
        setEditedValue(application.description ?? '');
      } else {
        setEditedValue('');
      }
      setEditingKey(key);
    },
    [application]
  );

  const handleCancelEdit = useCallback(() => {
    setEditedValue('');
    setEditingKey(null);
  }, []);

  const saveField = useCallback(
    async (overrides: Partial<UpdateApplicationRequest>, onSuccess: () => void) => {
      setIsSaving(true);
      try {
        const result = await updateApplication(buildUpdatePayload(application, overrides));
        await mutate(cacheKey, result, { revalidate: false });
        onSuccess();
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      } finally {
        setIsSaving(false);
      }
    },
    [application, cacheKey, mutate, updateApplication]
  );

  const handleSave = useCallback(
    async (key: string, value?: string) => {
      const valueToSave = (value ?? editedValue).trim();
      const apiFieldName = FIELD_NAME_MAP[key];

      if (!apiFieldName) return;

      if (apiFieldName === 'name') {
        const result = applicationNameSchema.safeParse(valueToSave);
        if (!result.success) {
          toast(ToastFactory.validationError(result.error.issues[0].message));
          return;
        }
      }

      const overrides =
        apiFieldName === 'description'
          ? { description: valueToSave || null }
          : { name: valueToSave };

      await saveField(overrides, () => setEditingKey(null));
    },
    [editedValue, saveField, toast]
  );

  const ag2AppIdBody = isEditingAg2AppId ? (
    <Flex align="center" gap={2}>
      <Ag2AppIdSelect
        ag2ProductId={workspace?.enterpriseArchitectureProductId}
        isDisabled={isSaving || isWorkspaceLoading}
        onChange={setPendingAg2AppId}
        selectedAg2AppId={pendingAg2AppId}
        width="260px"
      />
      <EditButtons
        isEditing
        isSaving={isSaving}
        onCancel={() => {
          setPendingAg2AppId(application.ag2AppId);
          setIsEditingAg2AppId(false);
        }}
        onSave={() => {
          void saveField({ ag2AppId: pendingAg2AppId }, () => setIsEditingAg2AppId(false));
        }}
        saveDisabled={
          isSaving ||
          isWorkspaceLoading ||
          !workspace?.enterpriseArchitectureProductId ||
          pendingAg2AppId === application.ag2AppId
        }
      />
    </Flex>
  ) : (
    <Flex align="center" gap={2}>
      <Box as="span">
        {application.ag2AppId ? (
          <Link href={`${AG2_BASE_URL}/${application.ag2AppId}/overview`} isExternal>
            {application.ag2AppId}
          </Link>
        ) : (
          '-'
        )}
      </Box>
      {canWrite && (
        <EditButtons
          editDisabled={isWorkspaceLoading || !workspace?.enterpriseArchitectureProductId}
          editLabel="Edit AG 2.0"
          isEditing={false}
          isSaving={false}
          onCancel={() => {}}
          onEdit={() => {
            setPendingAg2AppId(application.ag2AppId);
            setIsEditingAg2AppId(true);
          }}
          onSave={() => {}}
        />
      )}
    </Flex>
  );

  const applicationDetailsSections = [
    {
      title: 'General',
      items: {
        ID: { body: application.id },
        Description: { body: application.description || '-' },
        'AG 2.0': {
          body: ag2AppIdBody,
        },
        'Workspace ID': {
          body: <WorkspaceTextLink workspaceId={application.workspaceId} />,
        },
        'Created By': { body: <UserNameLink oid={application.createdByOid} /> },
        Created: { body: new Date(application.createdAt).toLocaleString(dateTimeFormat.local) },
        'Updated By': { body: <UserNameLink oid={application.updatedByOid} /> },
        Updated: { body: new Date(application.updatedAt).toLocaleString(dateTimeFormat.local) },
      },
    },
    {
      title: 'Versions',
      items: {
        'Latest Version': {
          body: application.latestVersionId ? (
            <Link
              as={ReactRouterLink}
              to={paths.applicationVersionById(
                application.workspaceId,
                application.id,
                application.latestVersionId
              )}
            >
              {application.latestVersionId}
            </Link>
          ) : (
            'No versions exist'
          ),
        },
        'Stable Version': {
          body: isEditingStableVersion ? (
            <Flex align="center" gap={2}>
              <StableVersionSelect
                isDisabled={isSaving}
                onChange={setPendingStableVersionId}
                selectedVersionId={pendingStableVersionId}
                versions={versions}
                width="260px"
              />
              <EditButtons
                isEditing
                isSaving={isSaving}
                onCancel={() => {
                  setPendingStableVersionId(application.stableVersionId);
                  setIsEditingStableVersion(false);
                }}
                onSave={() => {
                  void saveField({ stableVersionId: pendingStableVersionId }, () =>
                    setIsEditingStableVersion(false)
                  );
                }}
                saveDisabled={isSaving || pendingStableVersionId === application.stableVersionId}
              />
            </Flex>
          ) : (
            <Flex align="center" gap={2}>
              <Box as="span">
                {application.stableVersionId ? (
                  <Link
                    as={ReactRouterLink}
                    to={paths.applicationVersionById(
                      application.workspaceId,
                      application.id,
                      application.stableVersionId
                    )}
                  >
                    {application.stableVersionId}
                  </Link>
                ) : (
                  'No stable version set'
                )}
              </Box>
              {canWrite && (
                <EditButtons
                  editDisabled={versions.length === 0}
                  editLabel="Edit stable version"
                  isEditing={false}
                  isSaving={false}
                  onCancel={() => {}}
                  onEdit={() => {
                    setPendingStableVersionId(application.stableVersionId);
                    setIsEditingStableVersion(true);
                  }}
                  onSave={() => {}}
                />
              )}
            </Flex>
          ),
        },
      },
    },
  ] as DetailSectionProps[];

  return (
    <EditableDetailsBox
      editableFields={canWrite ? ['Name', 'Description'] : []}
      editedValue={editedValue}
      editingKey={editingKey}
      isSaving={isSaving}
      onCancelEdit={handleCancelEdit}
      onSave={handleSave}
      onStartEdit={handleStartEdit}
      onValueChange={setEditedValue}
      sections={applicationDetailsSections}
      title={application.name}
    />
  );
};
